import { utils } from "@intrasync/utils";
import React from "react";
import {
  FormControl,
  FormHelperText,
  Input as MUIInput,
  InputAdornment,
  InputLabel,
  Paper,
} from "@mui/material";
import injectStores from "../RootStore/injectStores";
import { useStyles } from "./inputStyle";
import { ViewStore } from "stores/index";

interface IInputProps {
  viewStore?: ViewStore;
  labelText?: string;
  type?: string;
  value?: string;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange: (value: string) => void;
  error?: boolean;
  success?: boolean;
  white?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  rows?: number;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  placeholder?: string;
  margin?: "dense" | "none" | "normal";
  readOnly?: boolean;
  id?: string;
  inputSX?: Record<string, string>;
  formControlSX?: Record<string, string>;
  disableUnderline?: boolean;
  size?: "small" | "medium";
  min?: number | string;
  max?: number | string;
}

const Input = injectStores((props: IInputProps) => {
  const classes = useStyles();
  const {
    viewStore,
    labelText,
    type,
    value,
    onKeyPress,
    onChange,
    error,
    success,
    white,
    helperText,
    fullWidth,
    required,
    disabled,
    autoFocus,
    multiline,
    minRows,
    maxRows,
    rows,
    endAdornment,
    startAdornment,
    placeholder,
    margin = "normal",
    readOnly,
    id,
    inputSX,
    formControlSX,
    disableUnderline,
    min,
    max,
  } = props;
  let { size = "medium" } = props;

  const isMobile = viewStore?.isMobile ?? false;

  if (isMobile) {
    console.log("trigger autosize");
    size = "small";
  }

  const inputId = id || utils.uuid();

  const EndAdornment = endAdornment || undefined;
  const StartAdornment = startAdornment || undefined;

  const labelClasses = `${error ? classes.labelRootError : ""} ${
    success && !error ? classes.labelRootSuccess : ""
  }`;
  const underlineClasses = `${error ? classes.underlineError : ""} ${
    success && !error ? classes.underlineSuccess : ""
  } ${classes.underline}`;
  const inputLabelClasses = `${labelClasses} ${classes.labelRoot}`;
  const inputClasses = `${classes.input} ${
    white ? classes.whiteInputWithBlackPlaceholder : ""
  }`;
  const helpTextClasses = `${!success && !error ? classes.labelRootBase : ""} ${
    !success && !error && white ? classes.labelRootWhiteBase : ""
  } ${error ? classes.labelRootError : ""} ${
    success && !error ? classes.labelRootSuccess : ""
  }`;
  const formControlClasses = classes.formControl;
  const renderFormControl = () => (
    <FormControl
      variant="standard"
      fullWidth={fullWidth}
      className={formControlClasses}
      margin={white ? "none" : margin}
      sx={{
        ...formControlSX,
        ...(white && {
          height: "100%",
          justifyContent: "center",
        }),
      }}
    >
      {labelText !== undefined ? (
        <InputLabel className={inputLabelClasses} htmlFor={id}>
          {labelText}
        </InputLabel>
      ) : null}
      <MUIInput
        placeholder={placeholder}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        readOnly={readOnly}
        rows={rows}
        disableUnderline={disableUnderline}
        sx={
          white
            ? {
                ...inputSX,
                "& .MuiInput-input": {
                  padding: "0px",
                },
              }
            : {
                marginTop: "8px",
                ...inputSX,
              }
        }
        classes={{
          input: inputClasses,
          disabled: classes.disabled,
          underline: disableUnderline ? "" : underlineClasses,
        }}
        id={inputId}
        endAdornment={
          !!EndAdornment && (
            <InputAdornment className={classes.inputAdornment} position="end">
              {EndAdornment}
            </InputAdornment>
          )
        }
        startAdornment={
          !!StartAdornment && (
            <InputAdornment className={classes.inputAdornment} position="start">
              {StartAdornment}
            </InputAdornment>
          )
        }
        inputProps={{
          type: type,
          value: value || "",
          onKeyPress: onKeyPress || undefined,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(e?.target?.value || ""),
          ...(type === "number" && min !== undefined && { min }),
          ...(type === "number" && max !== undefined && { max }),
        }}
      />
      {helperText !== undefined ? (
        <FormHelperText id={id + "-helperText"} className={helpTextClasses}>
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
  if (white) {
    const height = size === "small" ? "31px" : "41px";
    return (
      <Paper
        sx={{
          padding: "0px 8px",
          height,
          display: "flex",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
        }}
        elevation={1}
      >
        {renderFormControl()}
      </Paper>
    );
  }
  return renderFormControl();
});

export default Input;
